// INTENT: Primary JavaScript entrypoint for main application layout.
// EXPECTED OUTCOME: Loads error tracking strictly scoped to Stimulus controllers and Turbo runtime.
// CAVEAT/TRAP: Propshaft handles static asset serving directly from app/javascript/videos; explicit require.context is unnecessary.
import Appsignal from "@appsignal/javascript"
import { installErrorHandler } from "@appsignal/stimulus"

const appsignalKeyMeta = document.querySelector('meta[name="appsignal-frontend-key"]')
const appsignalRevisionMeta = document.querySelector('meta[name="appsignal-revision"]')
const appsignalKey = appsignalKeyMeta ? appsignalKeyMeta.getAttribute("content") : null

if (appsignalKey) {
  // INTENT: Instantiate AppSignal for frontend error tracking.
  // EXPECTED OUTCOME: Configures AppSignal client without attaching to global window.onerror or unhandled rejections.
  // CAVEAT/TRAP: Global window listeners capture noisy 3rd-party ads and extension errors; scoping directly to Stimulus isolates reporting to first-party application logic.
  window.appsignal = new Appsignal({
    key: appsignalKey,
    namespace: "frontend",
    revision: appsignalRevisionMeta ? appsignalRevisionMeta.getAttribute("content") : ""
  })
} else {
  // INTENT: If key is blank (e.g. in test environment), disable frontend error reporting.
  // EXPECTED OUTCOME: Prevents noisy reporting errors during spec execution.
  // CAVEAT/TRAP: window.appsignal remains null.
  window.appsignal = null
}

import { Application } from "@hotwired/stimulus"
import DropdownController from './controllers/dropdown_controller'
import ToggleController from './controllers/toggle_controller'
import FlashController from './controllers/flash_controller'
import DismissibleController from './controllers/dismissible_controller'
import InstructionController from './controllers/instruction_controller'
import TimezoneController from './controllers/timezone_controller'
import AutosizeController from './controllers/autosize_controller'
import DropdownMenuController from '../components/become_ui/dropdown/component.js'
import TimerController from './controllers/timer_controller'
import StripeController from './controllers/stripe_controller'
import CountdownController from './controllers/countdown_controller'
import ExamMarqueeController from "./controllers/exam_marquee_controller"
import HoverHighlightController from "./controllers/hover_highlight_controller"
import AsyncChartController from "./controllers/async_chart_controller"
import SidebarScrollController from "./controllers/sidebar_scroll_controller"
import SchedulingController from "./controllers/scheduling_controller"
import InfoBlocksController from './controllers/info_blocks_controller'
import BranchVisibilityController from './controllers/branch_visibility_controller'
import StudentFilterController from './controllers/student_filter_controller'
import CharacterCounterController from './controllers/character_counter_controller'
import ModalController from './controllers/modal_controller'
import PrintReportController from './controllers/print_report_controller'
import WaFormController from './controllers/wa_form_controller'
import ExitSurveyController from './controllers/exit_survey_controller'
import StudyGuideProgressController from './controllers/study_guide_progress_controller'
import CheckoutConfirmationController from './controllers/checkout_confirmation_controller'
import CheckAllController from './controllers/check_all_controller'

window.Stimulus = Application.start()
if (window.appsignal) {
  installErrorHandler(window.appsignal, window.Stimulus)
}
Stimulus.register("character-counter", CharacterCounterController)
Stimulus.register("dropdown", DropdownController)
Stimulus.register("toggle", ToggleController)
Stimulus.register("flash", FlashController)
Stimulus.register("dismissible", DismissibleController)
Stimulus.register("instruction", InstructionController)
Stimulus.register("timezone", TimezoneController)
Stimulus.register("autosize", AutosizeController)
Stimulus.register("dropdownmenu", DropdownMenuController)
Stimulus.register("timer", TimerController)
Stimulus.register("stripe", StripeController)
Stimulus.register("countdown", CountdownController)
Stimulus.register("exam-marquee", ExamMarqueeController)
Stimulus.register("hover-highlight", HoverHighlightController)
Stimulus.register("async-chart", AsyncChartController)
Stimulus.register("sidebar-scroll", SidebarScrollController)
Stimulus.register("scheduling", SchedulingController)
Stimulus.register("info-blocks", InfoBlocksController)
Stimulus.register("branch-visibility", BranchVisibilityController)
Stimulus.register("student-filter", StudentFilterController)
Stimulus.register("modal", ModalController)
Stimulus.register("print-report", PrintReportController)
Stimulus.register("exit-survey", ExitSurveyController)
Stimulus.register("study-guide-progress", StudyGuideProgressController)
Stimulus.register("checkout-confirmation", CheckoutConfirmationController)
Stimulus.register("check-all", CheckAllController)
// // INTENT: Register wa-form Stimulus controller for Web Awesome input validation and loading spinners.
// // EXPECTED OUTCOME: Forms using data-controller="wa-form" gain custom input validation and automatic button loading spinners on submit.
// // CAVEAT/TRAP: Required for Web Awesome forms rendered in standard application layout pages like account settings.
Stimulus.register("wa-form", WaFormController)

import "@hotwired/turbo-rails"
Turbo.session.drive = false

require('tocbot');
